/**
 * Content predicates for the top-level bands of the encounter summary report
 * (Subjective / Objective / Assessment / Plan / …).
 *
 * `ReportSection` prints its header unconditionally while the blocks nested
 * inside it self-hide when they have no data. For note types whose form does
 * not expose those fields at all — Collaborative Care Model and Psychoeducation
 * Therapy capture neither the SOAP free notes nor the clinical blocks — that
 * left a bare band with nothing under it in the printed note.
 *
 * Each predicate below mirrors the guard of every block the section renders, so
 * a band is printed only when at least one of its blocks will render something.
 * Keep them in step with the blocks they mirror.
 */

const hasText = (val?: unknown) =>
  typeof val === "string" ? val.trim().length > 0 : !!val;

/** Non-empty check that works for both the list and map shaped payloads. */
const hasRows = (val?: unknown) => {
  if (!val) return false;
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === "object") return Object.keys(val).length > 0;
  return true;
};

/**
 * Vital rows reach the report as the full placeholder set
 * (`ENCOUNTER_INITIAL_VITALS`) even when nothing was recorded, so a length check
 * would always pass. Mirrors how VitalsCommonTable flattens a row into readings.
 */
const VITAL_VALUE_FIELDS = [
  "bloodGlucose",
  "heartRate",
  "respirationRate",
  "height",
  "weight",
  "bodyTemperature",
  "o2Saturation",
  "bmi",
  "pulse",
  "peakExpiratoryFlow",
  "forceExpiratoryVolume",
  "perfusionIndex",
  "respiratoryVolume",
];

const rowHasVital = (row: any) => {
  // Format 1 — flat { name, value }; blood pressure still needs both readings.
  if (row?.name && row?.value) {
    return row.name === "bloodPressure"
      ? !!(row?.systolic && row?.diastolic)
      : true;
  }
  // Format 2 — one key per vital.
  if (row?.systolic && row?.diastolic) return true;
  return VITAL_VALUE_FIELDS.some((field) => !!row?.[field]);
};

const hasVitals = (data: any) =>
  (data?.patientVitals ?? []).some(rowHasVital);

export const hasSubjectiveContent = (data: any) =>
  hasText(data?.historyOfPresentIllness) ||
  hasText(data?.subjectiveFreeNote) ||
  hasRows(data?.patientAllergies) ||
  hasRows(data?.medicalHistories) ||
  hasRows(data?.surgicalHistory) ||
  hasRows(data?.familyHistories) ||
  hasRows(data?.patientSocialHistory) ||
  hasText(data?.habits) ||
  hasRows(data?.patientMedications);

export const hasObjectiveContent = (data: any) =>
  hasText(data?.objectiveFreeNote) ||
  hasVitals(data) ||
  hasRows(data?.physicalTemplates) ||
  hasRows(data?.rosTemplates) ||
  hasRows(data?.patientQuestionAnswers);

/** Assessment free note + the ICD codes rendered by `AssessmentBlock`. */
export const hasAssessmentContent = (data: any) =>
  hasText(data?.assessmentFreeNote) || hasRows(data?.assessmentCodes);

/** CPT codes rendered by `CptCodesBlock`, which templates place either inside
 *  Plan (most notes) or in a closing Clinical Diagnoses band (Psychoeducation),
 *  so it is gated separately from the rest of the Plan blocks. */
export const hasCptCodes = (data: any) => hasRows(data?.procedureCodes);

/** Every Plan block except the CPT codes — see `hasCptCodes`. */
export const hasPlanContent = (data: any) =>
  hasText(data?.planFreeNote) ||
  hasRows(data?.patientPrescriptionForms) ||
  hasRows(data?.patientVaccines) ||
  hasRows(data?.patientOrders) ||
  hasText(data?.carePlan) ||
  hasRows(data?.carePlanTemplates) ||
  hasText(data?.instruction);

export const hasFollowUp = (data: any) => hasText(data?.followUp);

/** Fields captured by the Psychoeducation Therapy form, mirroring the
 *  sub-sections of `PsychoeducationBlock`. */
const PSYCHOEDUCATION_FIELDS = [
  "primaryTopic",
  "learningObjectives",
  "contentDelivered",
  "skillsCategoriesCovered",
  "skillsDescription",
  "materialsDistributed",
  "materialDetails",
  "engagementLevel",
  "comprehensionLevel",
  "questionsDiscussion",
  "participantFeedback",
  "inSessionPractice",
  "homePracticeAssignments",
  "realLifeApplicationPlan",
  "followUpTopics",
  "additionalResourcesNeeded",
  "nextSessionPlan",
  "psychoeducationProviderNote",
];

export const hasPsychoeducationContent = (data: any) =>
  PSYCHOEDUCATION_FIELDS.some((field) => hasText(data?.[field]));
